import { useEffect, useRef, useState } from 'react';

type Options = {
  /**
   * Minimum scroll-Y delta (px) before the hidden state can flip. Filters
   * out trackpad inertia / rubber-band jitter that would otherwise toggle
   * the bar on every micro-scroll.
   */
  threshold?: number;
  /**
   * Scroll position below which the bar is always shown. Keep this at or
   * just above the bar's own height so the bar can never hide itself out
   * of view while the user is reading the top of the page.
   */
  topPin?: number;
  /**
   * Pin the bar visible (hidden = false) regardless of scroll direction.
   * Use this to suppress hide-on-scroll while a dropdown or drawer owned
   * by the bar is open — closing the bar mid-interaction kills the menu.
   */
  disabled?: boolean;
};

/**
 * Hide-on-scroll-down, show-on-scroll-up.
 *
 * Returns `hidden` — true when the consumer should slide the element out
 * of view. Designed for the fixed AppBar but reusable for any floating
 * top-of-page UI.
 */
export function useScrollDirection({
  threshold = 10,
  topPin = 64,
  disabled = false,
}: Options = {}): boolean {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(typeof window === 'undefined' ? 0 : window.scrollY);

  useEffect(() => {
    if (disabled) {
      setHidden(false);
      return;
    }

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;

      if (Math.abs(delta) < threshold) return;

      if (y < topPin) {
        setHidden(false);
      } else if (delta > 0) {
        setHidden(true);
      } else {
        setHidden(false);
      }

      lastY.current = y;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold, topPin, disabled]);

  return hidden;
}
